import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const button = cva(
  [
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap",
    "font-medium select-none cursor-pointer",
    "transition-[background-color,border-color,color,box-shadow] duration-[var(--duration-micro)] ease-[var(--ease-out)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "[&_svg]:shrink-0 [&_svg]:size-4",
  ],
  {
    variants: {
      intent: {
        primary:
          "bg-accent text-fg-on-accent hover:bg-accent-hover shadow-1",
        secondary:
          "bg-surface text-fg border border-border-default hover:bg-surface-hover",
        ghost:
          "bg-transparent text-fg hover:bg-surface-hover",
        danger:
          "bg-danger-bold text-fg-on-accent hover:bg-danger-bold-hover shadow-1",
        link:
          "bg-transparent text-accent-text hover:underline underline-offset-2 px-0",
      },
      size: {
        sm: "h-7 px-2.5 text-xs rounded-[var(--radius-sm)]",
        md: "h-8 px-3 text-sm rounded-[var(--radius-md)]",
        lg: "h-10 px-4 text-sm rounded-[var(--radius-md)]",
      },
    },
    defaultVariants: {
      intent: "secondary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, intent, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type ?? "button"}
        className={cn(button({ intent, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
